import { memo } from 'react';
import { Query } from 'src/modules/common/components/Query/Query';
import { Filter } from 'src/utils/composeFilter';
import { FilterValuesGroup } from 'src/common/FilterValuesGroup/FilterValuesGroup';
import { DocumentNode, OperationVariables, skipToken } from '@apollo/client';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler';
import { AttributeValueWithCount } from 'src/generated/operations';
import { Comparator } from 'src/common/types/Comparator.ts';
import { FlowUnitsRadioButtons } from 'src/sensors/FlowUnitsRadioButtons/FlowUnitsRadioButtons.tsx';

type Props<Data> = {
    query: DocumentNode;
    filter: Filter;
    variables: typeof skipToken | {
        variables: Data;
    };
    focusedFilterName: string | null;
    name: string;
    comparator: Comparator;
    checkboxHandler: CheckboxHandler;
    setFocusedFilterName: (name: string | null) => void;
    selectedFlowRange?: string;
    onFlowRangeSelected?: (name: string) => void;
}

export const FilterItemQuery = memo(<
    Data extends {filter: { values: Array<AttributeValueWithCount>}},
    Variables extends OperationVariables
>({
    query,
    filter,
    variables,
    focusedFilterName,
    name,
    comparator,
    checkboxHandler,
    setFocusedFilterName,
    selectedFlowRange,
    onFlowRangeSelected,
}: Props<Variables>) => (
    <Query<Data, Variables>
        query={query}
        variables={variables}
    >
        {({ data }) => {
            if (data === undefined) {
                return null;
            }

            const { values } = data.filter;
            const filterSelected = filter[name] || [];
            if (values.length === 0 && filterSelected.length === 0) {
                return (<div className="vss-filter--empty"/>);
            }
            const isDisabled = focusedFilterName !== null && focusedFilterName !== name;

            let subHeader = null;

            if (name.includes('FlowRange')) {
                if (selectedFlowRange !== name || onFlowRangeSelected === undefined) {
                    return (<div className="vss-filter--empty"/>);
                }

                subHeader = (
                    <FlowUnitsRadioButtons
                        attribute={name}
                        checked={name}
                        onChange={onFlowRangeSelected}
                    />
                );
            }

            return (
                <FilterValuesGroup
                    filter={filter}
                    name={name}
                    comparator={comparator}
                    options={values}
                    onChange={checkboxHandler}
                    disabled={isDisabled}
                    setFocusedFilterName={setFocusedFilterName}
                    subHeader={subHeader}
                />
            );
        }}
    </Query>
));
